/**
 * PIFster Countdown
 *
 * Strict TypeScript conversion for the monthly transition countdown.
 */

interface CountdownParts {
  readonly days: number;
  readonly hrs: number;
  readonly mins: number;
  readonly secs: number;
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function splitDiff(seconds: number): CountdownParts {
  const totalSeconds = seconds < 0 ? 0 : seconds;
  const days = Math.floor(totalSeconds / 86400);
  const remAfterDays = totalSeconds % 86400;
  const hrs = Math.floor(remAfterDays / 3600);
  const remAfterHours = remAfterDays % 3600;
  const mins = Math.floor(remAfterHours / 60);
  const secs = remAfterHours % 60;

  return { days, hrs, mins, secs };
}

function verboseLabel(seconds: number): string {
  const { days, hrs, mins, secs } = splitDiff(seconds);
  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
  }

  if (hrs > 0) {
    parts.push(`${hrs} ${hrs === 1 ? 'hour' : 'hours'}`);
  }

  if (mins > 0) {
    parts.push(`${mins} ${mins === 1 ? 'minute' : 'minutes'}`);
  }

  if (secs >= 0) {
    parts.push(`${secs} ${secs === 1 ? 'second' : 'seconds'}`);
  }

  return `${parts.join(', ')} remaining`;
}

function formatDiff(seconds: number): string {
  const { days, hrs, mins, secs } = splitDiff(seconds);

  if (days > 0) {
    return `${days}${days === 1 ? ' day ' : ' days '}${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  }

  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
}

function clamp01(value: number): number {
  if (value < 0) {
    return 0;
  }

  if (value > 1) {
    return 1;
  }

  return value;
}

function tick(el: HTMLElement, endISO: string, startISO: string | null): boolean {
  try {
    const end = new Date(endISO);
    if (Number.isNaN(end.getTime())) {
      return false;
    }

    const start = startISO ? new Date(startISO) : null;
    const now = new Date();
    const diff = Math.floor((end.getTime() - now.getTime()) / 1000);

    if (diff <= 0) {
      el.textContent = 'Transition in progress…';
      el.classList.remove('is-urgent');
      el.setAttribute('aria-label', 'Transition in progress');
      window.setTimeout(() => {
        window.location.reload();
      }, 5000);
      return true;
    }

    const parts = splitDiff(diff);
    const daysNode = el.querySelector<HTMLElement>('[data-countdown-unit="days"]');
    const hrsNode = el.querySelector<HTMLElement>('[data-countdown-unit="hours"]');
    const minsNode = el.querySelector<HTMLElement>('[data-countdown-unit="minutes"]');
    const secsNode = el.querySelector<HTMLElement>('[data-countdown-unit="seconds"]');

    if (daysNode && hrsNode && minsNode && secsNode) {
      daysNode.textContent = String(parts.days);
      hrsNode.textContent = pad(parts.hrs);
      minsNode.textContent = pad(parts.mins);
      secsNode.textContent = pad(parts.secs);
    } else {
      el.textContent = formatDiff(diff);
    }

    el.setAttribute('aria-label', verboseLabel(diff));

    if (diff < 86400) {
      el.classList.add('is-urgent');
    } else {
      el.classList.remove('is-urgent');
    }

    const rail = el.parentElement?.querySelector<HTMLElement>('.plb-progress__remaining') ?? null;
    if (rail && start && !Number.isNaN(start.getTime())) {
      const totalMs = end.getTime() - start.getTime();
      if (totalMs > 0) {
        const elapsedMs = now.getTime() - start.getTime();
        const elapsedRatio = clamp01(elapsedMs / totalMs);
        rail.style.width = `${(elapsedRatio * 100).toFixed(2)}%`;
      }
    }
  } catch {
    return false;
  }

  return false;
}

function initCountdown(): void {
  const nodes = document.querySelectorAll<HTMLElement>('.pifster-countdown[data-end-iso]');
  if (nodes.length === 0) {
    return;
  }

  nodes.forEach((el: HTMLElement) => {
    const iso = el.getAttribute('data-end-iso');
    const startIso = el.getAttribute('data-start-iso');
    if (!iso) {
      return;
    }

    if (tick(el, iso, startIso)) {
      return;
    }

    const timer = window.setInterval(() => {
      const done = tick(el, iso, startIso);
      if (done) {
        window.clearInterval(timer);
      }
    }, 1000);

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          window.clearInterval(timer);
        }
      },
      { once: true }
    );
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCountdown);
} else {
  initCountdown();
}

export {};
